/**
 * The only file that knows vanilla-calendar-pro (DEVDV-683). Its own input-mode popup is not used: the calendar
 * renders into the popup the text field owns.
 */
import {Calendar} from 'vanilla-calendar-pro';

// Without bounds the window must still contain the value (D23); the library's own default floor is 1970.
const FLOOR = '1900-01-01';
const CEILING = '2099-12-31';
// The library writes years unpadded and reparses them with new Date(): nothing below year 1000 renders.
const RENDERABLE = '1000-01-01';
const LAST = '9999-12-31';

/**
 * @param {HTMLElement} host
 * @param {{value: string, min: string, max: string, locale: string, firstWeekday: number, texts: Object, onSelect: function(string): void}} options
 * @returns {{focus: function(): void, destroy: function(): void}}
 */
export function createCalendar(host, {value, min, max, locale, firstWeekday, texts, onSelect}) {
	const element = document.createElement('div');
	element.className = 'date-picker-calendar';
	host.append(element);

	const floor = value >= RENDERABLE ? earlier(FLOOR, value) : FLOOR;
	const ceiling = later(CEILING, value);
	// A single bound beyond the default window moves the open edge, so the window never inverts.
	const dateMin = later(min || (max && max < floor ? RENDERABLE : floor), RENDERABLE);
	const dateMax = max || (min && min > ceiling ? LAST : ceiling);
	const shown = clamp(value || currentMonth(), dateMin, dateMax);
	const outOfRange = Boolean(value) && (value < dateMin || value > dateMax);

	const calendar = new Calendar(element, {
		locale,
		firstWeekday:         firstWeekday % 7,
		selectedTheme:        'light',
		dateMin,
		dateMax,
		selectedDates:        value ? [value] : [],
		selectedMonth:        Number(shown.slice(5, 7)) - 1,
		selectedYear:         Number(shown.slice(0, 4)),
		selectionDatesMode:   'single',
		selectionMonthsMode:  true,
		selectionYearsMode:   true,
		enableDateToggle:     false,
		displayDatesOutside:  false,
		labels:               {
			application: texts.calendar,
			navigation:  texts.calendar,
			arrowPrev:   {month: texts.previousMonth, year: texts.previousYears},
			arrowNext:   {month: texts.nextMonth, year: texts.nextYears},
			month:       texts.selectMonth,
			months:      texts.monthList,
			year:        texts.selectYear,
			years:       texts.yearList,
			week:        texts.weekdays,
			dates:       texts.dates,
		},
		// The month grid shows abbreviations but announces full names (WCAG 2.5.3 Label in Name, F9).
		onCreateMonthEls(self, monthElement) {
			const month = monthElement.querySelector('[data-vc-months-month]');

			if (!month) {
				throw new Error('vanilla-calendar-pro changed its month grid (F9)');
			}

			month.textContent = month.getAttribute('aria-label') ?? month.textContent;
		},
		onClickDate(self) {
			const picked = self.context.selectedDates[0];

			if (!picked) {
				throw new Error('vanilla-calendar-pro reported a click without a selected date');
			}

			onSelect(picked);
		},
	});
	calendar.init();

	return {
		focus() {
			// A value outside the bounds was clamped into them: its month opens on the first enabled day (D23).
			const preferred = outOfRange ? null : (
				element.querySelector('[data-vc-date-selected] [data-vc-date-btn]:not([disabled])')
				?? element.querySelector('[data-vc-date-today] [data-vc-date-btn]:not([disabled])')
			);
			const target = preferred ?? element.querySelector('[data-vc-date-btn]:not([disabled])');
			target?.focus();
		},
		destroy() {
			calendar.destroy();
			// The library swaps the element for a clean clone on destroy, so empty the host instead.
			host.replaceChildren();
		},
	};
}

// ISO strings compare correctly as text.
function earlier(a, b) {
	return b && b < a ? b : a;
}

function later(a, b) {
	return b && b > a ? b : a;
}

// The library does not move the shown month into the bounds itself (D23).
function clamp(iso, min, max) {
	if (iso < min) {
		return min;
	}

	return iso > max ? max : iso;
}

function currentMonth() {
	const now = new Date();

	return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
}
